import * as React from 'react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Switch } from '@/src/shadComponents/ui/switch';
import useWebhookSettings, {
    AuthType,
    WebhookSettings,
} from '@/src/Activations/components/hooks/useWebhookSettings';

/*
 * Webhooks — one per activation, fired on every share (ADMIN-MERGE
 * Phase C: classic's "Integrations" tab was exactly this, renamed to
 * what it is and folded into Sharing, where the event it fires on
 * lives). Auth values are write-only: never echoed back, cleared
 * after save.
 */

const AUTH_LABELS: Record<AuthType, string> = {
    none: 'None',
    bearer: 'Bearer token',
    basic: 'Basic auth',
    custom_header: 'Custom header',
};

const FIELD_LABEL =
    'mb-1.5 block text-[12px] font-semibold text-muted-foreground';

export default function WebhookSection({
    activationId,
}: {
    activationId: number;
}) {
    const {
        getWebhookSettings,
        createWebhookSettings,
        updateWebhookSettings,
        deleteWebhookSettings,
        testWebhook,
    } = useWebhookSettings();

    const [settings, setSettings] = useState<WebhookSettings | null>(null);
    const [loaded, setLoaded] = useState(false);
    const [denied, setDenied] = useState(false);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState({
        webhookUrl: '',
        enabled: true,
        authType: 'none' as AuthType,
        authValue: '',
        customHeaderName: '',
    });
    const [busy, setBusy] = useState(false);
    const [testResult, setTestResult] = useState<{
        success: boolean;
        message: string;
    } | null>(null);
    const [formError, setFormError] = useState<string | null>(null);

    useEffect(() => {
        let mounted = true;
        getWebhookSettings(activationId)
            .then((data) => {
                if (!mounted) return;
                setSettings(data);
                setLoaded(true);
            })
            .catch(() => {
                /* 401 = server-side admin gate — hide quietly */
                if (mounted) {
                    setDenied(true);
                    setLoaded(true);
                }
            });
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activationId]);

    if (!loaded || denied) return null;

    const openEdit = () => {
        setDraft({
            webhookUrl: settings?.webhookUrl ?? '',
            enabled: settings?.enabled ?? true,
            authType: settings?.authType ?? 'none',
            authValue: '',
            customHeaderName: settings?.customHeaderName ?? '',
        });
        setFormError(null);
        setTestResult(null);
        setEditing(true);
    };

    const save = async () => {
        if (busy) return;
        if (!/^https?:\/\/.+/.test(draft.webhookUrl.trim())) {
            setFormError('The webhook URL must be a full URL.');
            return;
        }
        if (
            draft.authType === 'custom_header' &&
            !draft.customHeaderName.trim()
        ) {
            setFormError('Custom header auth needs a header name.');
            return;
        }
        setBusy(true);
        setFormError(null);
        const body: any = {
            webhookUrl: draft.webhookUrl.trim(),
            enabled: draft.enabled,
            authType: draft.authType,
        };
        if (draft.authValue) body.authValue = draft.authValue;
        if (draft.authType === 'custom_header')
            body.customHeaderName = draft.customHeaderName.trim();
        const result = settings
            ? await updateWebhookSettings(activationId, body).catch(
                  () => null,
              )
            : await createWebhookSettings(activationId, body).catch(
                  () => null,
              );
        setBusy(false);
        if (result) {
            setSettings(result);
            setEditing(false);
            setDraft((d) => ({ ...d, authValue: '' }));
            toast.success('Webhook saved');
        } else {
            setFormError("Couldn't save the webhook.");
        }
    };

    const runTest = async () => {
        if (busy) return;
        setTestResult(null);
        const result = await testWebhook(activationId);
        setTestResult(result);
    };

    const remove = async () => {
        if (
            !window.confirm(
                'Delete this webhook? Shares stop notifying the endpoint immediately.',
            )
        )
            return;
        const ok = await deleteWebhookSettings(activationId).catch(
            () => false,
        );
        if (ok) {
            setSettings(null);
            setEditing(false);
            toast.success('Webhook deleted');
        } else {
            toast.error("Couldn't delete the webhook.");
        }
    };

    return (
        <section className="mt-10">
            <div className="ps-label mb-2">Webhooks</div>
            <p className="mb-3 max-w-[62ch] text-[12.5px] text-faint">
                Every share POSTs to your endpoint — destination, share
                identifiers, activation and gallery ids, share type,
                timestamp. One webhook per event.
            </p>

            {!settings && !editing && (
                <button
                    type="button"
                    onClick={openEdit}
                    className="ps-pill cursor-pointer px-4 font-[inherit] text-[12.5px] font-semibold text-foreground"
                >
                    Add webhook
                </button>
            )}

            {settings && !editing && (
                <div className="group flex items-center gap-3 border-t border-line py-[9px] text-[12.5px]">
                    <span
                        className={
                            'h-[7px] w-[7px] flex-none rounded-full ' +
                            (settings.enabled ? 'bg-good' : 'bg-off')
                        }
                    />
                    <span className="min-w-0 flex-1 truncate font-mono text-[11px]">
                        {settings.webhookUrl}
                    </span>
                    <span className="whitespace-nowrap font-mono text-[9px] font-semibold uppercase tracking-[0.1em] text-faint">
                        {AUTH_LABELS[settings.authType]}
                    </span>
                    <span className="flex gap-3 text-[12px] font-semibold opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                        <button
                            type="button"
                            onClick={runTest}
                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-muted-foreground hover:text-foreground"
                        >
                            test
                        </button>
                        <button
                            type="button"
                            onClick={openEdit}
                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-muted-foreground hover:text-foreground"
                        >
                            edit
                        </button>
                        <button
                            type="button"
                            title="Delete webhook"
                            onClick={remove}
                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-faint hover:text-attention"
                        >
                            ×
                        </button>
                    </span>
                </div>
            )}

            {testResult && !editing && (
                <p
                    className={
                        'mt-1.5 text-[12px] font-semibold ' +
                        (testResult.success
                            ? 'text-good'
                            : 'text-attention')
                    }
                >
                    {testResult.success
                        ? 'Test delivered.'
                        : `Test failed — ${testResult.message}`}
                </p>
            )}

            {editing && (
                <div className="max-w-[480px] border-t border-line pt-3">
                    <label className={FIELD_LABEL}>Webhook URL</label>
                    <input
                        autoFocus
                        className="ps-input mb-3 w-full font-mono text-[12px]"
                        placeholder="https://hooks.zapier.com/hooks/catch/…"
                        value={draft.webhookUrl}
                        onChange={(e) =>
                            setDraft((d) => ({
                                ...d,
                                webhookUrl: e.target.value,
                            }))
                        }
                    />
                    <div className="mb-3 grid grid-cols-2 gap-3">
                        <div>
                            <label className={FIELD_LABEL}>
                                Authentication
                            </label>
                            <select
                                className="ps-input w-full cursor-pointer appearance-none text-[13px]"
                                value={draft.authType}
                                onChange={(e) =>
                                    setDraft((d) => ({
                                        ...d,
                                        authType: e.target
                                            .value as AuthType,
                                    }))
                                }
                            >
                                {(
                                    Object.entries(AUTH_LABELS) as [
                                        AuthType,
                                        string,
                                    ][]
                                ).map(([value, label]) => (
                                    <option key={value} value={value}>
                                        {label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {draft.authType === 'custom_header' && (
                            <div>
                                <label className={FIELD_LABEL}>
                                    Header name
                                </label>
                                <input
                                    className="ps-input w-full font-mono text-[12px]"
                                    placeholder="X-API-Key"
                                    value={draft.customHeaderName}
                                    onChange={(e) =>
                                        setDraft((d) => ({
                                            ...d,
                                            customHeaderName:
                                                e.target.value,
                                        }))
                                    }
                                />
                            </div>
                        )}
                    </div>
                    {draft.authType !== 'none' && (
                        <>
                            <label className={FIELD_LABEL}>
                                {draft.authType === 'bearer'
                                    ? 'Bearer token'
                                    : draft.authType === 'basic'
                                    ? 'Credentials (user:pass)'
                                    : 'Header value'}
                            </label>
                            <input
                                type="password"
                                autoComplete="new-password"
                                className="ps-input mb-3 w-full font-mono text-[12px]"
                                placeholder={
                                    settings?.hasAuthConfigured
                                        ? 'unchanged unless set'
                                        : ''
                                }
                                value={draft.authValue}
                                onChange={(e) =>
                                    setDraft((d) => ({
                                        ...d,
                                        authValue: e.target.value,
                                    }))
                                }
                            />
                        </>
                    )}
                    <label className="mb-4 flex w-fit cursor-pointer items-center gap-2.5 text-[12.5px] text-muted-foreground">
                        <Switch
                            checked={draft.enabled}
                            onCheckedChange={(value) =>
                                setDraft((d) => ({
                                    ...d,
                                    enabled: value,
                                }))
                            }
                        />
                        Enabled
                    </label>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            disabled={busy || !draft.webhookUrl.trim()}
                            onClick={save}
                            className="cursor-pointer rounded-full border-0 bg-brand px-5 py-2 font-[inherit] text-[13px] font-semibold text-brand-foreground disabled:opacity-50"
                        >
                            {busy ? 'Saving…' : 'Save webhook'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setEditing(false)}
                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-muted-foreground"
                        >
                            Cancel
                        </button>
                        {formError && (
                            <span className="text-[12px] font-semibold text-attention">
                                {formError}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}
